import { useEffect } from "react";
import { letterSizeGuide, numberSizeGuide, sizeGuideNote } from "@/mocks/sizeGuide";

interface SizeGuideModalProps {
  open: boolean;
  group: "letter" | "number";
  onClose: () => void;
}

export default function SizeGuideModal({ open, group, onClose }: SizeGuideModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const rows = group === "number" ? numberSizeGuide : letterSizeGuide;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Guia de medidas"
    >
      <button
        type="button"
        aria-label="Fechar guia de medidas"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/50 backdrop-blur-[3px]"
      />
      <div className="ab-modal-in relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl bg-background-50 sm:mx-4 sm:max-w-2xl sm:rounded-lg">
        <header className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4 md:px-7">
          <div>
            <h3 className="font-heading text-2xl text-foreground-900">Guia de medidas</h3>
            <p className="mt-1 text-xs text-foreground-600 md:text-sm">
              Confira as medidas para escolher o tamanho ideal para você.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 md:px-7">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] uppercase tracking-[0.14em] text-foreground-600">
                  <th className="rounded-l-md px-4 py-3 font-medium">Tamanho</th>
                  <th className="px-4 py-3 font-medium">Busto</th>
                  <th className="px-4 py-3 font-medium">Cintura</th>
                  <th className="px-4 py-3 font-medium">Quadril</th>
                  <th className="rounded-r-md px-4 py-3 font-medium">Comprimento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.size} className="border-b border-background-200 text-foreground-800">
                    <td className="px-4 py-3 font-medium text-foreground-900">{row.size}</td>
                    <td className="px-4 py-3">{row.bust}</td>
                    <td className="px-4 py-3">{row.waist}</td>
                    <td className="px-4 py-3">{row.hip}</td>
                    <td className="px-4 py-3">{row.length ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-5 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4 text-xs leading-relaxed text-foreground-700">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
              <i className="ri-information-line text-base text-secondary-700" />
            </span>
            {sizeGuideNote}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}